import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress, 
    Typography,
    Backdrop
} from '@material-ui/core';


import { Col, Row } from 'react-bootstrap';
import { getDocusignConfig, getAllAccount, getAllTemplatesByAccount, updateDocusignConfig, deleteDocusignConfig } from 'actions/user';
import { FormProvider, useForm } from 'react-hook-form';
import DropDownNew from 'components/forms/ReactHookForm/DropDown/DropDownNew';
import { Toast } from 'components/shared/Toaster';

function DocuSignForm() {
    const [docusignUrl, setDocusignUrl] = useState();
    const [templates, setTemplates] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState('');
    const [templateId, setTemplateId] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reauthRequired, setReauthRequired] = useState(false);

        const [loading, setLoading] = useState(false);


    const methods = useForm({
        mode: 'onChange',
        defaultValues: {
            accountId: '',
            templateId: '',
        }
    });

    const { control, handleSubmit, formState: { isValid } } = methods;

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [partnerIK, setPartnerIK] = useState('test');
    const [loginRedirectUri, setLoginRedirectUri] = useState('');
    const [locale, setLocale] = useState('en-US');
    
    useEffect(() => {
        const script = document.createElement('script');
        script.src = "https://www.docusign.com/scripts/ds-digital-signup.js";
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    useEffect(() => {
        fetchDocusignConfig();
    }, []);

    const fetchDocusignConfig = async () => {
    setLoading(true);
    try {
        const response = await getDocusignConfig();

        if (response?.response?.url) {
        setDocusignUrl(response.response.url);
        setReauthRequired(!!response.response.reauthRequired);
        return;
        }

        setDocusignUrl(undefined);
        setReauthRequired(false);

        const accountResponse = await getAllAccount();
        setAccounts(accountResponse.response || []);

        const accId = response?.response?.accountId;
        if (accId) setAccountId(accId);

        if (accId) {
        const templatesResponse = await getAllTemplatesByAccount(accId);
        setTemplates(templatesResponse.response || []);
        setTemplateId(response.response.invoiceFactoringAgreementTemplateId || '');

        if ((templatesResponse.response || []).length === 0) {
            Toast({ type: "error", message: "No templates found for the selected account." });
        }
        } else {
        setTemplates([]);
        setTemplateId('');
        }
    } catch (error) {
        console.error("Error fetching DocuSign URL:", error);
        await showConnectIfAccessExpired(error);
    } finally {
        setLoading(false);
    }
    };

    // A 409 means Docusign no longer accepts the stored credentials, so the account has to be authorized again.
    // The config endpoint hands back the consent URL once that happens, which flips this screen to the connect view.
    const showConnectIfAccessExpired = async (error) => {
        if (error?.status !== 409) {
            return;
        }
        try {
            const response = await getDocusignConfig();
            if (response?.response?.url) {
                setDocusignUrl(response.response.url);
                setReauthRequired(!!response.response.reauthRequired);
            }
        } catch (configError) {
            console.error("Error fetching DocuSign URL:", configError);
        }
    };


    const loadTemplate = (accountId) => {
        if (!!accountId) {
            getAllTemplatesByAccount(accountId)
                .then((templatesResponse) => {
                    setTemplates(templatesResponse.response || []);
                })
                .catch((error) => {
                    console.error("Error fetching templates by account:", error);
                    showConnectIfAccessExpired(error);
                });
        } else {
            setTemplates([]);
        }
    };

    const onSubmit = (data) => {
        updateDocusignConfig({
            accountId: data.accountId,
            invoiceFactoringAgreementTemplateId: data.templateId
        }).then(() => {
            Toast({
                type: "success", message: "Docusign config updated successfully."
            });
        }).catch((error) => {
            console.error("Error updating DocuSign config:", error);
        });
    };

    const handleRedirect = () => {
        if (docusignUrl) {
            window.location.href = docusignUrl;
        }
    };

    const handleRemoveAccount = () => {
        deleteDocusignConfig().then(() => {
            fetchDocusignConfig();
            Toast({
                type: "success",
                message: "The account has been removed."
            });
            setAccountId('');
            setTemplateId('');
            setTemplates([]);
            setDialogOpen(false);
        }).catch((error) => {
            Toast({
                type: "error",
                message: "Failed to remove the account. Please try again."
            });
        });

    };

    return (
        <Card className='flex justify-center items-center cardHeight' style={{ position: 'relative' }}>
            
            {loading ? <div className='d-flex align-items-center justify-content-center h-100'><CircularProgress size={48} color='primary' /></div> : 

            <Row className="p-2" style={{ width: '100%' }}>
                {!!docusignUrl && reauthRequired && (
                <Col md={12} className="text-center" style={{ marginTop: '50px' }}>
                    <Typography variant="body1">
                        Your DocuSign access expired and envelopes cannot be sent until it is restored.
                        Connect the account again to grant access.
                    </Typography>
                </Col>
                )}
                {!!docusignUrl && (
                <Col md={12} className="text-center" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: reauthRequired ? '20px' : '50px' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        onClick={handleRedirect}
                    >
                        Connect With My DocuSign Account
                    </Button>

                    <Button
                        onClick={() => {
                            if (window.DSDigitalSignup && typeof window.DSDigitalSignup.startSignup === 'function') {
                                window.DSDigitalSignup.startSignup(
                                    firstName,
                                    lastName,
                                    email,
                                    phone,
                                    partnerIK,
                                    "http://localhost:3000/settings/docusign",
                                    locale
                                );
                            } else {
                                console.error("Digital Referral script not loaded yet.");
                            }
                        }}
                        variant="contained"
                        style={{
                            backgroundColor: "#4B227C",
                            color: "#fff",
                            fontWeight: "bold"
                        }}
                    >
                        Create a New DocuSign Account & Start Free Trial
                    </Button>
                </Col>

                )}
                {!docusignUrl && (
                    <Col md={12}>
                        <FormProvider {...methods}>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <DropDownNew
                                        control={control}
                                        options={[
                                            ...accounts.map(account => ({
                                                label: account.account_name || account.base_uri,
                                                value: account.account_id
                                            }))
                                        ]}
                                        label="Account"
                                        name="accountId"
                                        labelField="label"
                                        valueField="value"
                                        preSelectedValue={accountId}
                                        fullWidth
                                        rules={{ required: true }}
                                        onChange={value => loadTemplate(value)}
                                    />
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <DropDownNew
                                        control={control}
                                        options={[
                                            ...templates.map(template => ({
                                                label: template.name,
                                                value: template.templateId
                                            }))
                                        ]}
                                        label="Default template"
                                        name="templateId"
                                        labelField="label"
                                        valueField="value"
                                        preSelectedValue={templateId}
                                        fullWidth
                                        rules={{ required: true }}
                                    />
                                </div>
                                <Row className="p-2 justify-content-end" style={{ width: '100%' }}>
                                    <Col md="auto" className="d-flex flex-row align-items-center">
                                        <Button
                                            variant="contained"
                                            color="default"
                                            onClick={() => setDialogOpen(true)}
                                            style={{ marginRight: '10px' }}
                                        >
                                            Remove Account
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            disabled={!isValid}
                                        >
                                            Save
                                        </Button>
                                    </Col>
                                </Row>
                            </form>
                        </FormProvider>
                    </Col>
                )}
            </Row>}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            >
                <DialogTitle>Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove the selected account?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleRemoveAccount} variant="contained" color="default" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default DocuSignForm;
